import { useTranslation } from 'react-i18next';
import { Gem, Check } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * Le Missioni — la tasca dei traguardi.
 *
 * ⚠️ MOCKUP GRAFICO **del servizio Quest**, non una cosa a parte. Quello che si
 * vede qui è come si vedranno le `missions` che QLASHGeminiZ sta costruendo su
 * Quest (`GET /v1/missions`): stesso prodotto, disegnato prima di poterlo
 * collegare. Qui sotto ci sono numeri scritti a mano, non dati — quando
 * l'agganciamo si sostituisce `MISSIONI` con quello che risponde il servizio e
 * il resto regge.
 *
 * Il nome è `missions` ovunque — chiavi i18n, tasca, componente — per stare
 * sullo STESSO namespace del servizio. Nel repo TMA `challenges` significa già
 * le Sfide di QDuels, e una terza parola per la stessa cosa avrebbe reso
 * l'aggancio un lavoro di traduzione invece che di collegamento.
 *
 * Come si legge una missione: una FAMIGLIA di traguardi (giocare tornei,
 * vincerli, vincere partite classificate) con quattro gradini — 3, 10, 25, 50.
 * Il titolo dice il gradino a cui si sta puntando ORA, la barra quanto manca
 * per arrivarci, i quattro rombi sono la scala intera, e sotto ogni rombo c'è
 * la ricompensa in gemme.
 *
 * Perché i rombi sono immagini e non componenti: sono ILLUSTRAZIONI, con
 * sfaccettature e ombre proprie, e ogni gradino ha la sua tavolozza (argento →
 * turchese → viola → oro). Ridisegnarle in codice per poterle ricolorare non
 * servirebbe: non devono prendere il colore del cuoio, sono premi, e un premio
 * si riconosce proprio perché ha un colore suo.
 *
 * ⚠️ Per il mockup lo stato SPENTO sta dentro il file: i disegni della famiglia
 * «tornei vinti» sono già grigi. Col servizio vivo servirà una variante per
 * stato di ogni gradino (oppure un filtro in grigio applicato al colorato),
 * perché lì acceso e spento dipendono da chi guarda.
 */

interface Missione {
  /** Prefisso dei file in `public/`: `<gradino>-<key>.svg`. */
  key: string;
  /** Il traguardo a cui si punta ora — è il titolo della card. */
  goal: string;
  /** Quanto manca al traguardo, da 0 a 1. */
  progress: number;
  /** I quattro gradini della scala. */
  tiers: readonly number[];
  /** Le gemme di ogni gradino. */
  rewards: readonly number[];
  /**
   * Quanti gradini sono già stati riscossi, da sinistra.
   *
   * Vale per le GEMME, non per i rombi: lo stato dei rombi in questo mockup è
   * già dentro il disegno (vedi sopra). I due devono raccontare la stessa cosa.
   */
  claimed: number;
}

const TIERS = [3, 10, 25, 50] as const;
const REWARDS = [50, 75, 100, 200] as const;

const MISSIONI: readonly Missione[] = [
  {
    key: 'tournaments-played',
    goal: 'Gioca 50 tornei',
    progress: 1,
    tiers: TIERS,
    rewards: REWARDS,
    claimed: 4,
  },
  {
    key: 'tournaments-win',
    goal: 'Vinci 3 tornei',
    progress: 0,
    tiers: TIERS,
    rewards: REWARDS,
    claimed: 0,
  },
  {
    key: 'ranked-win',
    goal: 'Vinci 10 battaglie ranked',
    progress: 0.07,
    tiers: TIERS,
    rewards: REWARDS,
    claimed: 1,
  },
];

export function Missions() {
  return (
    <div className="flex flex-col gap-3 pb-2">
      {MISSIONI.map((m) => (
        <CardMissione key={m.key} missione={m} />
      ))}
    </div>
  );
}

function CardMissione({ missione }: { missione: Missione }) {
  const { t } = useTranslation('general');

  return (
    <section
      aria-label={missione.goal}
      // `card-edge` dentro il cuoio è già la cucitura tratteggiata: la card
      // delle missioni non ha un contorno suo, ha quello di tutte le altre.
      className="card-edge flex flex-col gap-3 rounded-2xl bg-bg-secondary p-3"
    >
      <h3 className="text-sm font-bold text-fg">{missione.goal}</h3>

      <BarraProgresso value={missione.progress} />

      {/* Rombi e gemme sono DUE griglie a quattro colonne, non quattro colonne
          che contengono ognuna un rombo e una gemma: così i rombi si allineano
          fra loro anche se uno di essi ha un'altezza diversa, e le gemme
          restano su una riga sola invece di ballare in verticale. */}
      <div className="grid grid-cols-4 gap-2">
        {missione.tiers.map((tier) => (
          <img
            key={tier}
            src={`/${tier}-${missione.key}.svg`}
            alt={`${tier}`}
            draggable={false}
            className="w-full select-none"
          />
        ))}
      </div>

      {/* `pb-2`: la spunta sporge sotto la pastiglia, e senza respiro finirebbe
          tagliata dal bordo della card. */}
      <div className="grid grid-cols-4 gap-2 pb-2">
        {missione.rewards.map((gems, i) => (
          <PastigliaGemme
            key={missione.tiers[i]}
            gems={gems}
            claimed={i < missione.claimed}
            claimedLabel={t('missions_claimed', { defaultValue: 'Claimed' })}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * La barra del traguardo in corso.
 *
 * Il pieno è cuoio CHIARO e non l'accento dell'app: dentro lo zaino il turchese
 * non ha voce (ADR-0038, punto 12), e su un fondo di pelle scura la cosa più
 * chiara è già la più evidente — non serviva un colore diverso per dirlo.
 *
 * A zero non disegna nulla: una barra vuota con dentro un puntino sembra un
 * inizio di progresso che non c'è.
 */
function BarraProgresso({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className="h-1 w-full overflow-hidden rounded-full bg-pack-deep"
    >
      {pct > 0 && (
        <span
          aria-hidden
          className="block h-full rounded-full bg-pack-fg"
          style={{ width: `${pct}%` }}
        />
      )}
    </div>
  );
}

/**
 * La ricompensa di un gradino.
 *
 * Riscossa: contorno pieno e chiaro, con la spunta appesa al bordo inferiore.
 * Ancora da prendere: contorno cucito e testo smorzato — la stessa distinzione
 * fra «acceso» e «spento» che usa tutto il resto dello zaino.
 *
 * La gemma resta ROSA anche qui, mentre tutto il resto è cuoio: è la stessa
 * gemma della barra in alto, e una valuta che cambia colore a seconda di dove
 * la guardi smette di essere riconoscibile come valuta.
 */
function PastigliaGemme({
  gems,
  claimed,
  claimedLabel,
}: {
  gems: number;
  claimed: boolean;
  claimedLabel: string;
}) {
  return (
    <div className="relative flex justify-center">
      <span
        className={cn(
          'flex w-full items-center justify-center gap-1 rounded-lg px-1 py-1.5',
          'text-[11px] font-bold leading-none',
          claimed
            ? 'border border-pack-fg text-pack-fg'
            : 'border border-dashed border-pack-stitch text-pack-muted/60',
        )}
      >
        {gems}
        <Gem
          aria-hidden
          className={cn('h-3 w-3 shrink-0', claimed ? 'text-pink-400' : 'text-pack-muted/60')}
        />
      </span>

      {claimed && (
        <span
          aria-label={claimedLabel}
          className="absolute -bottom-1.5 left-1/2 flex h-3.5 w-3.5 -translate-x-1/2 items-center justify-center rounded-full bg-pack-fg text-pack-deep"
        >
          <Check aria-hidden className="h-2.5 w-2.5" strokeWidth={4} />
        </span>
      )}
    </div>
  );
}
